import React, { useState } from 'react';
import { Form, Badge } from 'react-bootstrap';
import {
    FaSearch,
    FaTimes,
    FaMapMarkerAlt,
    FaBriefcase,
    FaLock,
    FaCheckCircle,
    FaExchangeAlt,
    FaUserTie
} from 'react-icons/fa';
import useChangeProfessionalModal from './hooks/useChangeProfessionalModal';

// Même fallback SVG inline que ProfessionalCandidatePage : jamais cassé,
// aucune dépendance à un service externe.
const DEFAULT_AVATAR =
    "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23e9ecef'/%3E%3Ccircle cx='150' cy='115' r='55' fill='%23ced4da'/%3E%3Cellipse cx='150' cy='265' rx='95' ry='75' fill='%23ced4da'/%3E%3C/svg%3E";

const ChangeProfessionalModal = ({
                                     show,
                                     onClose,
                                     clientEOIId,
                                     currentProfessionalId,
                                     defaultCity,
                                     defaultService,
                                     onSuccess
                                 }) => {
    // ⚠️ Tous les Hooks doivent être appelés avant tout "return" conditionnel,
    // sinon React perd l'ordre des Hooks entre deux rendus ("show" qui bascule
    // de false à true) -> erreur react-hooks/rules-of-hooks.
    const {
        filteredProfessionals,
        onlyCurrentProfessionalMatches,
        isLoading,
        error,
        isSubmitting,
        selectedProfessionalId,
        searchTerm,
        setSearchTerm,
        handleSelectProfessional,
        handleConfirm,
        getProfessionalProfilePicture
    } = useChangeProfessionalModal({
        show,
        clientEOIId,
        currentProfessionalId,
        defaultCity,
        defaultService,
        onSuccess,
        onClose
    });

    if (!show) return null;

    const resolvePhoto = (pro) => getProfessionalProfilePicture(pro) || DEFAULT_AVATAR;

    return (
        <>
            <div
                className="cpm-overlay"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="cpm-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', display: 'flex', flexDirection: 'column',
                        animation: 'cpmSlideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="cpm-header-fixed">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                    <FaExchangeAlt style={{ color: 'var(--cpm-primary)' }} />
                                    Changer de prestataire
                                </h4>
                                <div className="text-muted small mt-1">
                                    Sélectionnez le nouveau professionnel à assigner
                                </div>
                            </div>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>
                    </div>

                    {/* Corps scrollable */}
                    <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }} className="p-4">

                        {/* ===== CRITÈRES VERROUILLÉS (ville / service) ===== */}
                        <div className="cpm-locked-filters mb-3">
                            <div className="small text-muted mb-2 d-flex align-items-center gap-1">
                                <FaLock size={11} /> Critères fixés pour cette demande
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                <span className="cpm-locked-chip">
                                    <FaMapMarkerAlt className="me-1" />
                                    {defaultCity || 'Toutes les villes'}
                                </span>
                                <span className="cpm-locked-chip">
                                    <FaBriefcase className="me-1" />
                                    {defaultService || 'Tous les services'}
                                </span>
                            </div>
                        </div>

                        {/* ===== RECHERCHE PAR NOM / PRÉNOM / RÉFÉRENCE ===== */}
                        <div className="mb-4">
                            <div className="cpm-search-bar d-flex align-items-center">
                                <span className="cpm-search-icon">
                                    <FaSearch />
                                </span>
                                <Form.Control
                                    type="text"
                                    placeholder="Rechercher par nom, prénom ou référence..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="cpm-search-input border-0 shadow-none py-2"
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="btn btn-link text-muted p-2"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ===== RÉSULTATS ===== */}
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="fw-bold mb-0" style={{ color: 'var(--cpm-primary-dark)' }}>
                                <FaUserTie className="me-2" />
                                Professionnels disponibles
                            </h6>
                            <Badge bg="light" text="dark" className="border">
                                {filteredProfessionals.length} résultat(s)
                            </Badge>
                        </div>

                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border" style={{ color: 'var(--cpm-primary)' }} role="status">
                                    <span className="visually-hidden">Chargement...</span>
                                </div>
                                <p className="mt-3 text-muted mb-0">Chargement des professionnels disponibles...</p>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger mb-0">{error}</div>
                        ) : filteredProfessionals.length === 0 ? (
                            <div className="text-center py-5">
                                <FaSearch size={40} className="text-secondary mb-3" />
                                {onlyCurrentProfessionalMatches ? (
                                    <>
                                        <p className="text-muted mb-1 fw-semibold">
                                            Aucun autre professionnel disponible avec ces critères pour le moment.
                                        </p>
                                        <p className="text-muted small mb-0">
                                            Le prestataire actuellement assigné est le seul à correspondre à cette ville et ce service.
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-muted mb-0">Aucun professionnel ne correspond à ces critères.</p>
                                )}
                            </div>
                        ) : (
                            <div className="row g-3">
                                {filteredProfessionals.map(pro => {
                                    const isSelected = String(selectedProfessionalId) === String(pro.id);
                                    return (
                                        <div key={pro.id} className="col-12 col-md-6">
                                            <div
                                                className={`cpm-pro-card ${isSelected ? 'cpm-pro-card-selected' : ''}`}
                                                onClick={() => handleSelectProfessional(pro.id)}
                                            >
                                                <div className="cpm-pro-photo-frame">
                                                    <img
                                                        src={resolvePhoto(pro)}
                                                        alt={`${pro.user?.firstName || ''} ${pro.user?.lastName || ''}`}
                                                        className="cpm-pro-photo-img"
                                                        onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR; }}
                                                    />
                                                    <div className="cpm-pro-photo-gradient" />

                                                    {isSelected && (
                                                        <div className="cpm-selected-badge">
                                                            <FaCheckCircle /> Sélectionné
                                                        </div>
                                                    )}

                                                    <div className="cpm-pro-photo-info">
                                                        <div className="fw-bold text-white text-truncate">
                                                            {pro.user?.firstName} {pro.user?.lastName}
                                                        </div>
                                                        <div className="text-white-50 small text-truncate">
                                                            {pro.selectedService || 'Service non spécifié'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="p-3">
                                                    <div className="cpm-pro-meta">
                                                        <FaMapMarkerAlt />
                                                        <span>{pro.city || pro.location || 'Ville non spécifiée'}</span>
                                                    </div>
                                                    <div className="cpm-pro-meta">
                                                        <FaBriefcase />
                                                        <span>{pro.selectedService || 'Service non spécifié'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer fixe */}
                    <div className="cpm-footer-fixed">
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="cpm-btn cpm-btn-outline"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={!selectedProfessionalId || isSubmitting}
                            className="cpm-btn cpm-btn-primary"
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" /> Changement en cours...
                                </>
                            ) : (
                                <>
                                    <FaCheckCircle className="me-2" /> Confirmer le changement
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                :root {
                    --cpm-primary: #667eea;
                    --cpm-primary-dark: #5a4fcf;
                    --cpm-secondary: #764ba2;
                    --cpm-border: #e2e5f1;
                    --cpm-surface-muted: #f4f5fb;
                }

                @keyframes cpmSlideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                @keyframes cpmFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .cpm-overlay {
                    animation: cpmFadeIn 0.3s ease;
                }

                .cpm-header-fixed {
                    flex: 0 0 auto;
                    position: sticky;
                    top: 0;
                    z-index: 100;
                    background-color: white;
                    border-bottom: 1px solid #e2e8f0;
                    border-top-left-radius: 24px;
                    border-top-right-radius: 24px;
                    padding: 16px 24px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .cpm-footer-fixed {
                    flex: 0 0 auto;
                    position: sticky;
                    bottom: 0;
                    background-color: white;
                    border-top: 1px solid #e2e8f0;
                    padding: 14px 24px;
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 12px;
                    flex-wrap: wrap;
                    box-shadow: 0 -2px 8px rgba(0,0,0,0.05);
                }

                .cpm-btn {
                    padding: 8px 24px;
                    border-radius: 50px;
                    font-size: 0.95rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    border: none;
                }
                .cpm-btn-outline {
                    border: 1px solid #6c757d;
                    background: transparent;
                    color: #6c757d;
                }
                .cpm-btn-outline:hover {
                    background-color: #6c757d;
                    color: white;
                }
                .cpm-btn-primary {
                    background: linear-gradient(135deg, var(--cpm-primary) 0%, var(--cpm-secondary) 100%);
                    color: white;
                }
                .cpm-btn-primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .cpm-btn-primary:not(:disabled):hover {
                    filter: brightness(1.05);
                }

                /* Critères verrouillés (ville / service) */
                .cpm-locked-filters {
                    background: var(--cpm-surface-muted);
                    border: 1px dashed var(--cpm-border);
                    border-radius: 0.75rem;
                    padding: 0.75rem 1rem;
                }
                .cpm-locked-chip {
                    background: #eef0fc;
                    color: var(--cpm-primary-dark);
                    border: 1px solid #d9defa;
                    border-radius: 50rem;
                    padding: 0.35rem 0.85rem;
                    font-size: 0.82rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                }

                /* Barre de recherche */
                .cpm-search-bar {
                    background: var(--cpm-surface-muted);
                    border: 1px solid var(--cpm-border);
                    border-radius: 1rem;
                    padding: 0.25rem;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }
                .cpm-search-bar:focus-within {
                    border-color: var(--cpm-primary);
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.18);
                    background: #fff;
                }
                .cpm-search-icon {
                    color: var(--cpm-primary);
                    display: flex;
                    align-items: center;
                    padding-left: 0.75rem;
                    padding-right: 0.5rem;
                }
                .cpm-search-input, .cpm-search-input:focus {
                    background: transparent !important;
                }

                /* Cards professionnel */
                .cpm-pro-card {
                    background: white;
                    border: 2px solid var(--cpm-border);
                    border-radius: 1rem;
                    overflow: hidden;
                    cursor: pointer;
                    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
                    height: 100%;
                }
                .cpm-pro-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(20, 20, 43, 0.12);
                }
                .cpm-pro-card-selected {
                    border-color: var(--cpm-primary);
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.18);
                }

                .cpm-pro-photo-frame {
                    position: relative;
                    width: 100%;
                    height: 140px;
                    background: var(--cpm-surface-muted);
                }
                .cpm-pro-photo-img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center top;
                }
                .cpm-pro-photo-gradient {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%);
                    pointer-events: none;
                }
                .cpm-pro-photo-info {
                    position: absolute;
                    left: 0; right: 0; bottom: 0;
                    padding: 10px 12px;
                }
                .cpm-selected-badge {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    background: linear-gradient(135deg, var(--cpm-primary) 0%, var(--cpm-secondary) 100%);
                    color: #fff;
                    font-size: 0.7rem;
                    font-weight: 600;
                    padding: 4px 10px;
                    border-radius: 50rem;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    box-shadow: 0 2px 8px rgba(20, 20, 43, 0.25);
                }

                .cpm-pro-meta {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #6c7293;
                    font-size: 0.85rem;
                    line-height: 1.4;
                    margin-bottom: 4px;
                }
                .cpm-pro-meta svg {
                    color: var(--cpm-primary);
                    min-width: 14px;
                    flex-shrink: 0;
                }

                ::-webkit-scrollbar { width: 6px; }
                ::-webkit-scrollbar-track { background: #f1f1f1; }
                ::-webkit-scrollbar-thumb { background: #888; border-radius: 3px; }
                ::-webkit-scrollbar-thumb:hover { background: #555; }
            `}</style>
        </>
    );
};

export default ChangeProfessionalModal;